import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Chips } from "primereact/chips";
import Popup from "../popup/Popup";
import { POPUP_FORM_CONTENT_CLASS } from "../popup/popupLayoutSpec";
import Heading from "../common/Typography/Heading";
import Paragraph from "../common/Typography/Paragraph";
import AuxiliaryText from "../common/Typography/AuxiliaryText";
import SpecialText from "../common/Typography/SpecialText";
import MainButton from "../common/MainButton/MainButton";
import StepBackButton from "../common/StepBackButton/StepBackButton";
import TextArea from "../common/InputField/TextArea";
import TextFiled from "../common/InputField/TextFiled";
import Snippet from "../common/SnippetIcon/Snippet";
import { FIELD_CHIPS_FULL } from "../common/InputField/fieldSystem";
import DropzoneUploader from "../common/DropzoneUploader/DropzoneUploader";
import RatingStars from "../common/RatingStars/RatingStars";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { disableScroll, enableScroll } from "../../utils/scrollUtils";
import { runTextPipeline } from "../../utils/contentSafety";

// Image Container Component
const ImageContainer = React.memo(({ url, name, isDragging }) => (
  <div className="relative size-full">
    <div 
      className="size-full bg-cover bg-center rounded-lg shadow-sm group-hover:shadow-md transition-all duration-200"
      style={{ 
        backgroundImage: `url(${url})`,
        opacity: isDragging ? 0.5 : 1
      }}
    />
    <div className="absolute inset-0 bg-transparent" />
  </div>
));

// Sortable Item Component
const SortableItem = ({ id, item, index, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = useMemo(() => ({
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : 'transform 200ms cubic-bezier(0.2, 0, 0, 1)',
    cursor: isDragging ? 'grabbing' : 'grab',
    touchAction: 'none',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    position: 'relative',
    zIndex: isDragging ? 1 : 'auto',
  }), [transform, transition, isDragging]);

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <div className="relative group">
        <div className="size-[60px]">
          <ImageContainer 
            url={item.url || ''} 
            name={item?.file?.name || 'Uploaded image'}
            isDragging={isDragging}
          />
          <button 
            type="button" 
            onClick={() => onDelete(index)}
            className="absolute -top-2 -right-2 size-5 flex items-center justify-center bg-white rounded-full shadow-md hover:bg-gray-100 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
          >
            <i className="fi fi-rs-cross text-[10px] text-offblack"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

// Guidelines Popup Component
const GuidelinesPopup = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]">
      <div className="bg-mistygray rounded-xl max-w-lg mx-4 max-h-[80vh] flex flex-col">
        {/* Fixed Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <Heading variant="h6" className="text-offblack">
            Trades Poster Review Guidelines
          </Heading>
          <button
            onClick={onClose}
            className="text-mediumgray hover:text-offblack transition-colors"
          >
            <i className="fi fi-rs-cross text-base"></i>
          </button>
        </div>
        
        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-10 py-6">
          <div className="space-y-4">
            <Paragraph variant="BodyCompact" className="text-offblack">
              These guidelines help keep Trades Poster reviews fair, respectful, and helpful. All users must follow the terms below when submitting feedback on a service provider.
            </Paragraph>
            
            <div>
              <Paragraph variant="BodyCompact" className="font-semibold text-offblack block mb-2">
                Keep It Real
              </Paragraph>
              <Paragraph variant="BodyCompact" className="text-offblack">
                Only share honest reviews based on your own personal experience. Reviews must reflect genuine interactions with the business.
              </Paragraph>
            </div>
            
            <div>
              <Paragraph variant="BodyCompact" className="font-semibold text-offblack block mb-2">
                Be Respectful
              </Paragraph>
              <Paragraph variant="BodyCompact" className="text-offblack">
                No offensive, abusive, or defamatory language. Keep your feedback professional — even when negative.
              </Paragraph>
            </div>
            
            <div>
              <Paragraph variant="BodyCompact" className="font-semibold text-offblack block mb-2">
                Stay Relevant
              </Paragraph>
              <Paragraph variant="BodyCompact" className="text-offblack">
                Don't include promotions, external links, contact details, or off-topic content. Stick to the service provided.
              </Paragraph>
            </div>
            
            <div>
              <Paragraph variant="BodyCompact" className="font-semibold text-offblack block mb-2">
                Use Real Photos Only
              </Paragraph>
              <Paragraph variant="BodyCompact" className="text-offblack">
                If uploading images, they must be your own and clearly show the actual job or service outcome. No stock, AI, or unrelated images.
              </Paragraph>
            </div>

            <div>
              <Paragraph variant="BodyCompact" className="font-semibold text-offblack block mb-2">
                We Moderate Reviews
              </Paragraph>
              <Paragraph variant="BodyCompact" className="text-offblack">
                Trades Poster may remove or hide reviews that breach these guidelines or Australian consumer content standards. Repeated misuse can lead to account restrictions.
              </Paragraph>
            </div>
          </div>
        </div>
        
        {/* Fixed Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end">
          <MainButton
            variant="small"
            onClick={onClose}
          >
            Got it
          </MainButton>
        </div>
      </div>
    </div>
  );
};

// Validation schema for the review form
const reviewValidationSchema = Yup.object().shape({
  overallRating: Yup.number()
    .min(1, 'Overall rating is required')
    .max(5, 'Overall rating cannot exceed 5')
    .required('Overall rating is required'),
  communication: Yup.number()
    .min(1, 'Communication rating is required')
    .max(5, 'Communication rating cannot exceed 5')
    .required('Communication rating is required'),
  reliability: Yup.number()
    .min(1, 'Reliability rating is required')
    .max(5, 'Reliability rating cannot exceed 5')
    .required('Reliability rating is required'),
  satisfaction: Yup.number()
    .min(1, 'Satisfaction rating is required')
    .max(5, 'Satisfaction rating cannot exceed 5')
    .required('Satisfaction rating is required'),
  recommend: Yup.number()
    .min(1, 'Recommend rating is required')
    .max(5, 'Recommend rating cannot exceed 5')
    .required('Recommend rating is required'),
  punctuality: Yup.number()
    .min(1, 'Punctuality rating is required')
    .max(5, 'Punctuality rating cannot exceed 5')
    .required('Punctuality rating is required'),
  servicesReceived: Yup.array()
    .min(1, 'Please add at least one service you received')
    .max(3, 'Maximum 3 services allowed')
    .required('Services received is required'),
  reviewText: Yup.string()
    .min(30, 'Review must be at least 30 characters')
    .max(1000, 'Review cannot exceed 1000 characters')
    .required('Review text is required')
    .test('contentSafety', function (value) {
      if (!value || !String(value).trim()) return true;
      const result = runTextPipeline(value, 'review');
      if (result.valid) return true;
      return this.createError({ message: result.message });
    }),
  photos: Yup.array().max(3, 'Maximum 3 photos allowed'),
  confirmExperience: Yup.boolean()
    .oneOf([true], 'You must confirm this review is based on your own experience')
    .required('Confirmation is required'),
  agreeGuidelines: Yup.boolean()
    .oneOf([true], 'You must agree to the review guidelines')
    .required('Agreement to guidelines is required')
});

const ReviewForm = ({ 
  isOpen, 
  onClose, 
  businessData = null 
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [showCrossButton, setShowCrossButton] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [images, setImages] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showGuidelines, setShowGuidelines] = useState(false);

  // Area ratings for step 2
  const areaRatings = [
    { key: 'communication', label: 'Communication' },
    { key: 'reliability', label: 'Reliability' },
    { key: 'satisfaction', label: 'Satisfaction' },
    { key: 'recommend', label: 'Recommend' },
    { key: 'punctuality', label: 'Punctuality' }
  ];

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const formik = useFormik({
    initialValues: {
      overallRating: 0,
      communication: 0,
      reliability: 0,
      satisfaction: 0,
      recommend: 0,
      punctuality: 0,
      servicesReceived: [],
      reviewText: "",
      photos: [],
      confirmExperience: false,
      agreeGuidelines: false
    },
    validationSchema: reviewValidationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        // TODO: Implement API call to submit review
        console.log('Submitting review:', values);
        await new Promise(resolve => setTimeout(resolve, 1000));
        setShowConfirmation(true);
      } catch (error) {
        console.error('Error submitting review:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  });

  // Handle step navigation
  const handleNext = () => {
    if (currentStep < 6) {
      setCurrentStep(currentStep + 1);
    } else {
      formik.handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Handle star rating changes
  const handleRatingChange = (fieldName, value) => {
    formik.setFieldValue(fieldName, value);
  };

  // Handle checkbox changes
  const handleCheckboxChange = (fieldName, value) => {
    formik.setFieldValue(fieldName, value);
  };

  // Handle image upload
  const handleUploadComplete = useCallback((uploadData) => {
    const newImages = Array.isArray(uploadData) ? uploadData : [uploadData];
    const processedImages = newImages.map((data, index) => ({
      name: data.fileName,
      url: data.url,
    }));
    
    setImages(prev => [...prev, ...processedImages]);
    
    const currentImages = formik.values.photos || [];
    const updatedImages = [...currentImages, ...processedImages];
    formik.setFieldValue("photos", updatedImages);
  }, [formik]);

  const deleteImage = useCallback((index) => {
    const newImages = images.filter((_, i) => i !== index);
    setImages(newImages);
    formik.setFieldValue("photos", newImages);
  }, [images, formik]);

  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id);
    setIsDragging(true);
  }, []);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      const oldIndex = images.findIndex((_, index) => `item-${index}` === active.id);
      const newIndex = images.findIndex((_, index) => `item-${index}` === over.id);
      
      const newItems = arrayMove(images, oldIndex, newIndex);
      
      setImages(newItems);
      formik.setFieldValue("photos", newItems);
    }
    
    setIsDragging(false);
    setActiveId(null);
  }, [images, formik]);

  const items = useMemo(() => 
    images.map((_, index) => `item-${index}`),
    [images]
  );

  // Get step title
  const getStepTitle = () => {
    switch (currentStep) {
      case 1: return "How would you rate this business?";
      case 2: return "Rate the following areas";
      case 3: return "Services received";
      case 4: return "Write Your Review";
      case 5: return "Upload Photos (Optional)";
      case 6: return "Confirm & Submit";
      default: return "";
    }
  };

  // Check if current step is valid
  const isCurrentStepValid = () => {
    switch (currentStep) {
      case 1:
        return formik.values.overallRating > 0;
      case 2:
        return areaRatings.every(area => formik.values[area.key] > 0);
      case 3:
        return formik.values.servicesReceived.length >= 1;
      case 4:
        return formik.values.reviewText.length >= 30;
      case 5:
        return true; // Photos are optional
      case 6:
        return formik.values.confirmExperience && formik.values.agreeGuidelines;
      default:
        return false;
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderOverallRatingStep();
      case 2:
        return renderAreaRatingsStep();
      case 3:
        return renderServicesReceivedStep();
      case 4:
        return renderReviewTextStep();
      case 5:
        return renderPhotosStep();
      case 6:
        return renderConfirmationStep();
      default:
        return null;
    }
  };

  // Step 1: Overall Rating
  const renderOverallRatingStep = () => (
    <div className="w-full">
      <div className="flex justify-center mb-6">
        <RatingStars
          rating={formik.values.overallRating}
          onRatingChange={(rating) => handleRatingChange('overallRating', rating)}
          size="35px"
          interactive={true}
        />
      </div>
      {formik.touched.overallRating && formik.errors.overallRating && (
        <SpecialText variant="FormValidation" className="text-alertred mt-2 text-center">
          {formik.errors.overallRating}
        </SpecialText>
      )}
    </div>
  );

  // Step 2: Area Ratings
  const renderAreaRatingsStep = () => (
    <div className="w-full max-w-full mx-auto space-y-4">
      {areaRatings.map((area) => (
        <div key={area.key} className="flex items-center justify-between">
          <Paragraph variant="BodyCompact" className="text-offblack">
            {area.label}
          </Paragraph>
          <RatingStars
            rating={formik.values[area.key]}
            onRatingChange={(rating) => handleRatingChange(area.key, rating)}
            size="large"
            interactive={true}
          />
        </div>
      ))}
      {formik.touched.communication && formik.errors.communication && (
        <SpecialText variant="FormValidation" className="text-alertred mt-2 text-center">
          Please rate all areas
        </SpecialText>
      )}
    </div>
  );

  // Step 3: Services Received
  const renderServicesReceivedStep = () => {
    const hasServices = formik.values.servicesReceived.length > 0;

    return (
    <div className="w-full min-w-0">
      <Snippet variant="label" label="What service did you receive?" star />
      <Chips
        id="servicesReceived"
        placeholder={hasServices ? "" : "e.g., Plumbing repair, Electrical installation, Kitchen renovation"}
        className={`input-placeholder toggle-chip ${FIELD_CHIPS_FULL}${hasServices ? " field-chips--expanded" : ""}`}
        value={formik.values.servicesReceived}
        onChange={(e) => {
          if (e.value.length <= 3) {
            formik.setFieldValue("servicesReceived", e.value);
          }
        }}
        onBlur={() => formik.setFieldTouched("servicesReceived", true)}
        separator=","
      />
      {formik.touched.servicesReceived && formik.errors.servicesReceived && (
        <SpecialText variant="FormValidation" className="text-alertred mt-2">
          {formik.errors.servicesReceived}
        </SpecialText>
      )}
              <div className="mt-2">
          <AuxiliaryText variant="BodyMeta" className="text-mediumgray">
            Type and press Enter to add services. Maximum 3 services allowed.
          </AuxiliaryText>
        </div>
    </div>
    );
  };

  // Step 4: Review Text
  const renderReviewTextStep = () => (
    <div className="w-full">
      <div className="relative">
        <TextArea
          variant="general"
          fullWidth
          label={null}
          placeholder="Share your experience with this business..."
          name="reviewText"
          value={formik.values.reviewText}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          className="min-h-[120px] resize-none pr-16"
          style={{ resize: 'none' }}
        />
        {/* <div className="absolute -top-3 right-1">
          <SpecialText variant="Counter" className="text-mediumgray">
            {formik.values.reviewText.length}/1000
          </SpecialText>
        </div> */}
      </div>
      {formik.touched.reviewText && formik.errors.reviewText && (
        <SpecialText variant="FormValidation" className="text-alertred mt-2">
          {formik.errors.reviewText}
        </SpecialText>
      )}
      <div className="mt-2">
        <AuxiliaryText variant="BodyMeta" className="text-mediumgray">
          Minimum 30 characters required
        </AuxiliaryText>
      </div>
    </div>
  );

  // Step 5: Photos
  const renderPhotosStep = () => (
    <div className="w-full">
      <div className="w-full">
        {/* Uploaded Images */}
        {images.length > 0 && (
          <div className="mt-1 mb-5 w-full">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={items}
                strategy={rectSortingStrategy}
              >
                <div className="flex flex-wrap gap-7">
                  {images.map((item, index) => (
                    <SortableItem
                      key={`item-${index}`}
                      id={`item-${index}`}
                      item={item}
                      index={index}
                      onDelete={deleteImage}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        )}

        <DropzoneUploader
          isImage={true}
          maxFiles={3}
          loadingType="progress"
          onUploadComplete={handleUploadComplete}
          currentFileCount={images.length}
          applyFieldStyles={false}
        />
      </div>
      <div className="mt-3">
        <AuxiliaryText variant="BodyMeta" className="text-mediumgray">
          Max 3 images • JPG / PNG / WEBP • Max 5MB per file
        </AuxiliaryText>
      </div>
    </div>
  );

  // Step 6: Confirmation
  const renderConfirmationStep = () => (
    <div className="w-full space-y-4">
      <div className="flex items-center gap-3">
        <label
          className="relative flex items-center rounded-full cursor-pointer"
          htmlFor="confirmExperience"
        >
          <input
            type="checkbox"
            id="confirmExperience"
            checked={formik.values.confirmExperience}
            onChange={(e) => handleCheckboxChange('confirmExperience', e.target.checked)}
            className="before:content[''] peer relative size-4 cursor-pointer appearance-none rounded-[4px] border border-mediumgray transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:h-12 before:w-12 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500 before:opacity-0 before:transition-opacity checked:border-primaryblue checked:bg-primaryblue checked:before:bg-primaryblue"
          />
          <span className="absolute text-white transition-opacity opacity-0 pointer-events-none top-[40%] left-2/4 -translate-y-2/4 -translate-x-2/4 peer-checked:opacity-100">
            <i className="fi fi-rs-check text-[10px]"></i>
          </span>
        </label>
        <label htmlFor="confirmExperience" className="input-label font-inter text-offblack cursor-pointer">
          I confirm this review is based on my own experience.
        </label>
      </div>
      
      <div className="flex items-center gap-3">
        <label
          className="relative flex items-center rounded-full cursor-pointer"
          htmlFor="agreeGuidelines"
        >
          <input
            type="checkbox"
            id="agreeGuidelines"
            checked={formik.values.agreeGuidelines}
            onChange={(e) => handleCheckboxChange('agreeGuidelines', e.target.checked)}
            className="before:content[''] peer relative size-4 cursor-pointer appearance-none rounded-[4px] border border-mediumgray transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:h-12 before:w-12 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500 before:opacity-0 before:transition-opacity checked:border-primaryblue checked:bg-primaryblue checked:before:bg-primaryblue"
          />
          <span className="absolute text-white transition-opacity opacity-0 pointer-events-none top-[40%] left-2/4 -translate-y-2/4 -translate-x-2/4 peer-checked:opacity-100">
            <i className="fi fi-rs-check text-[10px]"></i>
          </span>
        </label>
        <label htmlFor="agreeGuidelines" className="input-label font-inter text-offblack cursor-pointer">
          I agree to the{' '}
          <button
            type="button"
            onClick={() => setShowGuidelines(true)}
            className="text-primaryblue underline hover:no-underline"
          >
            review guidelines
          </button>
          .
        </label>
      </div>
      
      {(formik.touched.confirmExperience && formik.errors.confirmExperience) ||
       (formik.touched.agreeGuidelines && formik.errors.agreeGuidelines) && (
        <SpecialText variant="FormValidation" className="text-alertred mt-2">
          Please confirm both statements
        </SpecialText>
      )}
    </div>
  );

  // Handle popup close
  const handleClose = () => {
    onClose();
    formik.resetForm();
    setCurrentStep(1);
    setShowConfirmation(false);
    setImages([]);
  };

  // Update body overflow when popup is open
  useEffect(() => {
    if (isOpen) {
      disableScroll();
    } else {
      enableScroll();
    }
  }, [isOpen]);

  // Cleanup effect to ensure scrolling is restored on unmount
  useEffect(() => {
    return () => {
      enableScroll();
    };
  }, []);

  if (!isOpen) return null;

  // Render confirmation step
  if (showConfirmation) {
    return (
      <Popup
        setTrigger={() => handleClose()}
        ShowCrossButton={showCrossButton}
        onClose={handleClose}
      >
        <div className="overflow-auto relative flex items-center justify-center" style={{ minHeight: 'calc(100vh - 120px)' }}>
          <div className={POPUP_FORM_CONTENT_CLASS}>
            <Heading
              variant={"h6"}
              className={
                "flex items-center justify-center text-center text-offblack"
              }
            >
              Thank you
            </Heading>
            <div className="mt-10 xl:mt-8 w-full">
              <div className="flex justify-center w-full">
                <div className="w-full mx-auto">
                  <Paragraph
                    variant={"BodyCompact"}
                    className={"text-offblack text-center"}
                  >
                    Your review has been submitted successfully.
                  </Paragraph>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Popup>
    );
  }

  return (
    <>
      <GuidelinesPopup 
        isOpen={showGuidelines} 
        onClose={() => setShowGuidelines(false)} 
      />
      
      <Popup
        setTrigger={() => handleClose()}
        ShowCrossButton={showCrossButton}
        onClose={handleClose}
      >
        <div className="overflow-auto relative flex items-center justify-center" style={{ minHeight: 'calc(100vh - 120px)' }}>
          <div className={POPUP_FORM_CONTENT_CLASS}>
            <Heading variant="h6" className="flex items-center justify-center text-center">
              {getStepTitle()}
            </Heading>
            <div className="mt-10 xl:mt-8 w-full">
              {renderStepContent()}
              <div className={`flex items-center justify-between ${currentStep === 1 ? 'mt-20' : 'mt-10'} w-full`}>
                <StepBackButton onClick={handleBack} />
                <MainButton
                  variant="small"
                  onClick={handleNext}
                  disabled={!isCurrentStepValid() || isSubmitting}
                >
                  {currentStep === 6 ? (isSubmitting ? 'Submit' : 'Submit') : 'Next'}
                </MainButton>
              </div>
            </div>
          </div>
        </div>
      </Popup>
    </>
  );
};

export default ReviewForm; 